export const RUNTIME_ACCESS_EVENT = "vp:runtime-access";
export const TRIAL_EXPIRED_READ_ONLY_CODE = "trial_expired_read_only";
export const TRIAL_CONFIGURATION_MISSING_REASON = "trial_configuration_missing";

const BILLING_PATH = "/app/billing";
const VALID_MODES = new Set(["full", "read_only"]);
const VALID_REASONS = new Set([null, "trial_expired", TRIAL_CONFIGURATION_MISSING_REASON]);
const VALID_CONFIGURATION_ERRORS = new Set([
  "trial_end_missing_or_invalid",
  "trial_start_evidence_missing_or_invalid",
  "trial_start_does_not_match_provisioning",
  "trial_end_must_follow_start",
  "trial_policy_missing_or_invalid",
  "trial_policy_version_mismatch",
  "trial_end_does_not_match_frozen_policy",
]);

function isRecord(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isIsoDate(value) {
  return typeof value === "string" && value.trim() !== "" && Number.isFinite(Date.parse(value));
}

/**
 * Validate the tenant runtime-access contract without deriving commercial state from any other
 * field. In particular, a subscription status or a generic HTTP 402 never implies read-only mode.
 */
export function normalizeRuntimeAccess(value) {
  if (!isRecord(value)) return null;
  if (!VALID_MODES.has(value.mode) || !VALID_REASONS.has(value.reason)) return null;
  if (typeof value.subscription_status !== "string" || value.subscription_status.trim() === "") return null;
  if (value.billing_path !== BILLING_PATH) return null;

  const trialEndsAt = value.trial_ends_at;
  if (trialEndsAt !== null && !isIsoDate(trialEndsAt)) return null;
  const configurationError = value.configuration_error;
  if (configurationError !== null && !VALID_CONFIGURATION_ERRORS.has(configurationError)) return null;
  if (value.reason !== null && value.mode !== "read_only") return null;
  if (value.mode === "read_only" && value.reason === "trial_expired") {
    // At the exact expiry boundary the request guard becomes read-only before the scheduler
    // persists `trial_expired`. Legacy expired tenants can also lack a historical end timestamp.
    // Accept only those two explicit backend states; never infer expiry from a generic 402.
    if (!["trial", "trial_expired"].includes(value.subscription_status)) return null;
    if (value.subscription_status === "trial" && !isIsoDate(trialEndsAt)) return null;
    if (configurationError !== null) return null;
  }
  if (value.mode === "read_only" && value.reason === TRIAL_CONFIGURATION_MISSING_REASON) {
    // The stored clock can exist and still conflict with the immutable start/policy evidence.
    // Preserve it only as raw context; configuration_error remains the authoritative reason.
    if (!["trial", "trial_expired"].includes(value.subscription_status)) return null;
    if (!VALID_CONFIGURATION_ERRORS.has(configurationError)) return null;
  }
  if (value.reason !== TRIAL_CONFIGURATION_MISSING_REASON && configurationError !== null) return null;

  return {
    mode: value.mode,
    reason: value.reason,
    subscription_status: value.subscription_status,
    trial_ends_at: trialEndsAt,
    configuration_error: configurationError,
    billing_path: value.billing_path,
  };
}

export function runtimeAccessFromSession(session) {
  return normalizeRuntimeAccess(session?.tenant?.runtime_access);
}

export function isExpiredTrialReadOnly(access) {
  return access?.mode === "read_only" && access?.reason === "trial_expired";
}

export function isTrialConfigurationMissingReadOnly(access) {
  return access?.mode === "read_only" && access?.reason === TRIAL_CONFIGURATION_MISSING_REASON;
}

export function isTrialReadOnly(access) {
  return isExpiredTrialReadOnly(access) || isTrialConfigurationMissingReadOnly(access);
}

/** Parse only the backend's explicit trial read-only 402 envelope. */
export function parseRuntimeAccessError(error) {
  if (Number(error?.response?.status) !== 402) return null;
  const detail = error?.response?.data?.detail;
  if (!isRecord(detail) || detail.code !== TRIAL_EXPIRED_READ_ONLY_CODE) return null;
  if (typeof detail.message !== "string" || detail.message.trim() === "") return null;

  const runtimeAccess = normalizeRuntimeAccess(detail.runtime_access);
  if (!isTrialReadOnly(runtimeAccess)) return null;

  return { message: detail.message, runtimeAccess };
}

/**
 * Promote an authoritative blocked-write response to every mounted tenant shell. The Axios error
 * remains rejected and its server response is not rewritten, so callers keep the exact payload.
 */
export function handleRuntimeAccessError(error) {
  const parsed = parseRuntimeAccessError(error);
  if (!parsed) return false;

  error.userMessage = parsed.message;
  error.runtimeAccess = parsed.runtimeAccess;

  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    window.dispatchEvent(new CustomEvent(RUNTIME_ACCESS_EVENT, { detail: parsed }));
  }
  return true;
}
